import React, { useEffect, useRef, useState } from "react";
import { IconButton, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import {
  CfcAccountLookupButtonSlot,
  CfcAccountLookupRow,
  CfcOrderingOnBehalfCallout,
  CfcOrderingOnBehalfClinic,
  CfcOrderingOnBehalfText,
  CfcProviderAutocomplete,
  CfcProviderInfoContent,
  CfcProviderInfoFields,
  CfcProviderInfoLoading,
  CfcProviderInfoRequiredAsterisk,
  CfcProviderInfoRequiredCopy,
  CfcProviderInfoTextField,
} from "./CfcStepProviderInfo.styles";
import { CfcFormData } from "./types";
import {
  GetAllNewReqLimsAccount,
  GetAllNewReqOrderingProviders,
} from "../NewRequisition/Services/NewReqService";
import type {
  NewReqClinicAccount,
  NewReqOrderingProvider,
  OrderingProviderLookupError,
} from "../NewRequisition/types";
import { useMslAccountLookup } from "./useMslAccountLookup";
import { Button } from "../shared/Button/Button";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";

interface CfcStepProviderInfoProps {
  formData: CfcFormData;
  onFormChange: (updates: Partial<CfcFormData>) => void;
}

const EMPTY_PROVIDER_INFO = {
  orderingHealthcareProviders: "",
  orderingHealthcareNPI: "",
  providerAuthorization: false,
};

const EMPTY_CLINIC_SELECTION = {
  clinicAccountNumber: "",
  isMobilePhlebotomyAccount: false,
  isMobilePhlebotomy: false,
  ...EMPTY_PROVIDER_INFO,
};

const formatOrderingProvider = (provider: NewReqOrderingProvider): string =>
  `${provider.physician_name} - NPI: ${provider.npi}`;
const NO_ORDERING_PROVIDERS_MESSAGE =
  "No eligible ordering providers are available for this clinic.";
const ACCOUNT_LOOKUP_HELPER_TEXT =
  "Enter the account you want to order on behalf of, or leave blank to use your own clinics.";
const ACCOUNT_LOOKUP_ERROR_MESSAGES: Record<
  OrderingProviderLookupError,
  string
> = {
  not_found:
    "We couldn't find a clinic with this account number. Check it or contact support.",
  ambiguous: "We couldn't load this clinic. Please contact support.",
  request_failed: "We couldn't load this clinic. Please try again.",
};
const ORDERING_ON_BEHALF_PREFIX = "You're now ordering on behalf of ";
const ORDERING_ON_BEHALF_SUFFIX = ".";

const focusSoon = (ref: React.RefObject<HTMLInputElement | null>): void => {
  setTimeout(() => ref.current?.focus(), 0);
};

export const CfcStepProviderInfo = ({
  formData,
  onFormChange,
}: CfcStepProviderInfoProps): React.ReactElement => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { enabled: isMslOrderingEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ProviderMslOrdering,
  );
  const canUseMslOrdering =
    isMslOrderingEnabled && Boolean(auth?.can_use_msl_ordering);
  const authToken = auth?.token;
  const [clinicAccounts, setClinicAccounts] = useState<NewReqClinicAccount[]>(
    [],
  );
  const [affiliatedProviders, setAffiliatedProviders] = useState<
    NewReqOrderingProvider[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const {
    accountNumberInput,
    setAccountNumberInput,
    resolvedAccount,
    isLookingUp,
    lookupError,
    lookupAccount,
    clearAccount,
  } = useMslAccountLookup(authToken, setLoggedOut);
  const accountNumberInputRef = useRef<HTMLInputElement | null>(null);
  const providerInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let isMounted = true;

    const getProviderInfoOptions = async () => {
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const [allClinicAccounts, allOrderingProviders] = await Promise.all([
        GetAllNewReqLimsAccount(authToken, setLoggedOut),
        GetAllNewReqOrderingProviders(authToken, setLoggedOut),
      ]);

      if (!isMounted) return;

      setClinicAccounts(allClinicAccounts);
      setAffiliatedProviders(allOrderingProviders);
      setIsLoading(false);
    };

    getProviderInfoOptions();

    return () => {
      isMounted = false;
    };
  }, [authToken, setLoggedOut]);

  useEffect(() => {
    if (clinicAccounts.length !== 1 || formData.clinicAccountNumber) return;

    const [clinic] = clinicAccounts;
    onFormChange({
      ...EMPTY_CLINIC_SELECTION,
      clinicAccountNumber: clinic.lims_id ?? "",
      isMobilePhlebotomyAccount: clinic.is_mobile_phlebotomy_account,
    });
  }, [clinicAccounts, formData.clinicAccountNumber, onFormChange]);

  const activeProviders = resolvedAccount
    ? resolvedAccount.providers
    : affiliatedProviders;
  const clinicOptions = clinicAccounts.map((clinic) => clinic.lims_id);

  const getClinicLabel = (clinicAccountNumber: string): string =>
    clinicAccounts.find((clinic) => clinic.lims_id === clinicAccountNumber)
      ?.sfname ?? "";

  const filteredOrderingProviders = formData.clinicAccountNumber
    ? activeProviders.filter(
        (provider) => provider.lims_id === formData.clinicAccountNumber,
      )
    : [];

  const providerOptions = filteredOrderingProviders.map(
    (provider) => provider.npi,
  );
  const hasSelectedClinic = Boolean(formData.clinicAccountNumber);
  const noProvidersForSelectedClinic =
    hasSelectedClinic && !isLookingUp && filteredOrderingProviders.length === 0;
  const isProviderSelectDisabled =
    !hasSelectedClinic || isLookingUp || noProvidersForSelectedClinic;

  let accountHelperText: string | undefined = ACCOUNT_LOOKUP_HELPER_TEXT;
  if (resolvedAccount) {
    accountHelperText = undefined;
  } else if (lookupError) {
    accountHelperText = ACCOUNT_LOOKUP_ERROR_MESSAGES[lookupError];
  }

  const getProviderLabel = (providerNpi: string): string => {
    const provider = filteredOrderingProviders.find(
      (orderingProvider) => orderingProvider.npi === providerNpi,
    );
    return provider ? formatOrderingProvider(provider) : "";
  };

  const handleClinicChange = (clinicAccountNumber: string | null) => {
    const selectedClinic = clinicAccounts.find(
      (clinic) => clinic.lims_id === clinicAccountNumber,
    );

    clearAccount();
    onFormChange({
      ...EMPTY_CLINIC_SELECTION,
      clinicAccountNumber: selectedClinic?.lims_id ?? "",
      isMobilePhlebotomyAccount:
        selectedClinic?.is_mobile_phlebotomy_account ?? false,
    });
  };

  const handleProviderChange = (providerNpi: string | null) => {
    const selectedProvider = filteredOrderingProviders.find(
      (provider) => provider.npi === providerNpi,
    );

    if (!selectedProvider) {
      onFormChange(EMPTY_PROVIDER_INFO);
      return;
    }

    onFormChange({
      orderingHealthcareProviders: selectedProvider.physician_name,
      orderingHealthcareNPI: selectedProvider.npi,
      providerAuthorization: false,
    });
  };

  const handleLookupAccount = async () => {
    if (!accountNumberInput.trim()) return;

    const account = await lookupAccount();
    if (!account) {
      onFormChange(EMPTY_CLINIC_SELECTION);
      return;
    }

    onFormChange({
      ...EMPTY_CLINIC_SELECTION,
      clinicAccountNumber: account.limsId,
      isMobilePhlebotomyAccount: account.isMobilePhlebotomyAccount,
    });
    if (account.providers.length > 0) focusSoon(providerInputRef);
  };

  const handleClearAccount = () => {
    clearAccount();
    onFormChange(EMPTY_CLINIC_SELECTION);
    focusSoon(accountNumberInputRef);
  };

  if (isLoading) {
    return (
      <CfcProviderInfoLoading
        role="status"
        aria-label="Loading provider information"
        aria-live="polite"
      >
        <FontAwesomeIcon icon={faSpinner} spin aria-hidden />
      </CfcProviderInfoLoading>
    );
  }

  return (
    <CfcProviderInfoContent>
      {resolvedAccount && (
        <CfcOrderingOnBehalfCallout
          role="status"
          data-testid="cfc-ordering-on-behalf"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden />
          <CfcOrderingOnBehalfText>
            {ORDERING_ON_BEHALF_PREFIX}
            <CfcOrderingOnBehalfClinic>
              {resolvedAccount.name}
            </CfcOrderingOnBehalfClinic>
            {ORDERING_ON_BEHALF_SUFFIX}
          </CfcOrderingOnBehalfText>
        </CfcOrderingOnBehalfCallout>
      )}

      <CfcProviderInfoRequiredCopy>
        Please fill all required fields marked with an asterisk (
        <CfcProviderInfoRequiredAsterisk>*</CfcProviderInfoRequiredAsterisk>)
      </CfcProviderInfoRequiredCopy>

      <CfcProviderInfoFields>
        {canUseMslOrdering && (
          <CfcAccountLookupRow>
            <CfcProviderInfoTextField
              fullWidth
              label="Clinic Account Number"
              placeholder="Enter a clinic account number"
              variant="standard"
              value={
                resolvedAccount ? resolvedAccount.limsId : accountNumberInput
              }
              disabled={isLookingUp}
              error={Boolean(lookupError)}
              helperText={accountHelperText}
              inputRef={accountNumberInputRef}
              InputProps={{
                readOnly: Boolean(resolvedAccount),
                endAdornment: resolvedAccount ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear clinic account number"
                      edge="end"
                      onClick={handleClearAccount}
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              onChange={(event) => setAccountNumberInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleLookupAccount();
                }
              }}
            />
            {!resolvedAccount && (
              <CfcAccountLookupButtonSlot>
                <Button
                  label="GO"
                  size="medium"
                  loading={isLookingUp}
                  disabled={!accountNumberInput.trim()}
                  onClick={handleLookupAccount}
                  testId="cfc-account-lookup-go"
                />
              </CfcAccountLookupButtonSlot>
            )}
          </CfcAccountLookupRow>
        )}

        {!resolvedAccount && (
          <CfcProviderAutocomplete
            disablePortal
            options={clinicOptions}
            value={formData.clinicAccountNumber || null}
            getOptionLabel={getClinicLabel}
            onChange={(_event, clinicAccountNumber) =>
              handleClinicChange(clinicAccountNumber)
            }
            renderInput={(params) => (
              <CfcProviderInfoTextField
                {...params}
                label="Clinic name"
                placeholder="Please select one"
                required
                variant="standard"
              />
            )}
          />
        )}

        <CfcProviderAutocomplete
          disablePortal
          disabled={isProviderSelectDisabled}
          options={providerOptions}
          value={formData.orderingHealthcareNPI || null}
          getOptionLabel={getProviderLabel}
          onChange={(_event, providerNpi) => handleProviderChange(providerNpi)}
          renderInput={(params) => (
            <CfcProviderInfoTextField
              {...params}
              error={noProvidersForSelectedClinic}
              helperText={
                noProvidersForSelectedClinic
                  ? NO_ORDERING_PROVIDERS_MESSAGE
                  : undefined
              }
              label="Select Ordering provider"
              placeholder={
                isLookingUp ? "Loading ordering providers" : "Please select one"
              }
              inputRef={providerInputRef}
              required
              variant="standard"
            />
          )}
        />
      </CfcProviderInfoFields>
    </CfcProviderInfoContent>
  );
};
